import React, { useState } from 'react';
import moment from 'moment';
import { IRecipientContactInformation } from '@gik/calendar/models/RecipientContactInformation';
import DateTimeUtils from '@gik/core/utils/DateTimeUtils';
import { grubhubOrderDaysBefore, grubhubCanOrderNow } from './GrubhubConstants';
import bemBlock from '@gik/core/utils/bemBlock';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { CopyAddressButton, SvgIcon } from '@gik/ui';
import GrubhubButton from '@gik/calendar/components/GrubhubButton/GrubhubButton';
import { ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { getRecipientContactInformation } from '@gik/calendar/utils/getRecipientContactInformation';
import { useInkind } from '@gik/api/inkinds/inkind';
import { CalendarClaimFooter } from '../../CalendarClaimFooter';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import ContactPageOrganizersForAddress from '@gik/calendar/components/ContactPageOrganizersForAddress';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '../../i18n/en';
import { IAddToCalendarLinks } from '@gik/calendar/components/AddToCalendarSelector';
import { AddToCalendarButton } from '@gik/calendar/components/AddToCalendarSelector/AddToCalendarButton';

interface GrubhubClaimSuccessProps extends React.HTMLAttributes<HTMLDivElement> {
  entry: ICalendarEntry;
  event: ICalendarEvent;
  grubhubUrl: string;
  addToCalendarLinks: IAddToCalendarLinks;
  buttonsPortal?: () => HTMLElement;
  isFollowing: boolean;
  // onBack?: () => void | Promise<void>;
  // onCancel?: () => void | Promise<void>;
}

const bem = bemBlock('grubhub-claim-success');

export default function GrubhubClaimSuccess({
  className,
  entry,
  event,
  grubhubUrl,
  buttonsPortal,
  addToCalendarLinks,
  isFollowing,
  // onBack = noop,
  // onCancel = noop,
  ...otherProps
}: GrubhubClaimSuccessProps): React.ReactElement {
  // store previous following value as this will update as soon as the user claims the item
  const [prevIsFollowing] = useState(isFollowing);
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);
  const { t } = useTranslation();
  const recipientContactInformation = inkind ? getRecipientContactInformation(inkind) : null;

  const canOrderNow = grubhubCanOrderNow(moment.utc(event.startsAt));
  const eventDate = moment.utc(event.startsAt).format('MMMM Do');
  const eventTime = !entry.allDay
    ? (
        moment.utc(event.startsAt).format(DateTimeUtils.Time12HourFormat) +
        '-' +
        moment.utc(event.endsAt).format(DateTimeUtils.Time12HourFormat)
      ).toLowerCase()
    : '';

  const _buttons = (
    <CalendarClaimFooter>
      <GrubhubButton url={grubhubUrl} canOrderNow={canOrderNow} />
    </CalendarClaimFooter>
  );

  if (!recipientContactInformation) return null;

  return (
    <div className={bem(null, ['grubhub'], className)} {...otherProps}>
      <div className={bem('check')}>
        <SvgIcon name="CheckOutline" size="2xl" />
      </div>
      <span className={bem('title')}>Meal claimed!</span>

      <AddToCalendarButton links={addToCalendarLinks} />

      {!prevIsFollowing && <p className={bem('description-muted')}>{t(translationKeys.youAreNowFollowing)}</p>}

      {canOrderNow
        ? RenderOrderNowContent({
            recipientContactInformation,
            eventDate,
            eventTime,
            inkindRouteId,
          })
        : renderOrderLaterContent(event, recipientContactInformation, eventDate, eventTime)}

      {renderPortal(_buttons, buttonsPortal)}
    </div>
  );
}

interface IRenderOrderNowContentProps {
  recipientContactInformation: IRecipientContactInformation;
  eventDate: string;
  eventTime: string;
  inkindRouteId: string;
}

const RenderOrderNowContent = ({ recipientContactInformation, eventDate, eventTime, inkindRouteId }): JSX.Element => {
  const { t } = useTranslation();
  const { address1, address2, cityAndState, postcode, fullName } = recipientContactInformation;
  const recipientAddressLine1 = [address1, address2].filter(v => v).join(', ');
  const recipientAddressLine2 = [cityAndState, postcode].join(' ');

  return (
    <div className={bem('order-now')}>
      <strong>Be sure to order your meal for:</strong>
      <div className={bem('date', ['highlight'])}>
        {eventDate}, {eventTime}
      </div>

      <p className={bem('description-muted')}>{t(translationKeys.emailedConfirmation)}</p>

      {address1 ? (
        <>
          <p>Copy and paste the recipient’s address into GrubHub search to find their local favorites:</p>
          <address>
            <strong>{fullName}</strong>
            <br />
            {recipientAddressLine1}
            <br />
            {recipientAddressLine2}
          </address>
          <CopyAddressButton
            variant="default-light"
            textToCopy={[recipientAddressLine1, recipientAddressLine2].join('\r\n')}
          />
          <br />
        </>
      ) : (
        <ContactPageOrganizersForAddress inkindRouteId={inkindRouteId} />
      )}
    </div>
  );
};

const renderOrderLaterContent = (
  event: ICalendarEvent,
  recipientContactInformation: IRecipientContactInformation,
  eventDate: string,
  eventTime: string
): JSX.Element => {
  const eventCanOrderFromDate = moment.utc(event.startsAt).startOf('day').subtract(grubhubOrderDaysBefore, 'days');
  return (
    <div className={bem('order-later')}>
      <strong className={bem('date')}>
        For {eventDate}, {eventTime}
      </strong>

      <p>
        You’ll be able to schedule your meal
        <br /> delivery on{' '}
        <span className={bem('date-inline', ['highlight'])}>
          {moment.utc(eventCanOrderFromDate).format('MMMM Do')},{' '}
          {moment.utc(eventCanOrderFromDate).format('YYYY').toUpperCase()}
        </span>
        .
      </p>

      {recipientContactInformation.address1 ? (
        <>
          <p className={bem('note-muted')}>we’ll also email you a reminder and the delivery address</p>
          <p>
            See what’s available in {recipientContactInformation.cityAndState}, so you can be ready on ordering day...
          </p>
        </>
      ) : (
        <>
          <p className={bem('note-muted')}>we’ll also email you a reminder</p>
        </>
      )}
    </div>
  );
};
